import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, Calendar, Megaphone } from "lucide-react";
import LanguageToggle from "@/components/LanguageToggle";
import {
  BOOKING_URL,
  STAFF_LOGIN_URL,
  MARKETING_WORKSPACE_URL,
  ASSETS,
} from "@/lib/constants";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [staffOpen, setStaffOpen] = useState(false);
  const staffMenuRef = useRef(null);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  // Close the Staff Portal dropdown when clicking outside / hitting Escape.
  useEffect(() => {
    if (!staffOpen) return undefined;
    const onDown = (e) => {
      if (staffMenuRef.current && !staffMenuRef.current.contains(e.target)) {
        setStaffOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setStaffOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [staffOpen]);

  const staffItems = [
    {
      href: STAFF_LOGIN_URL,
      label: "Pabau Portal",
      hint: "Schedules · client records",
      icon: Calendar,
      testid: "nav-staff-menu-pabau",
    },
    {
      href: MARKETING_WORKSPACE_URL,
      label: "Marketing Workspace",
      hint: "Proposed campaigns",
      icon: Megaphone,
      testid: "nav-staff-menu-marketing",
    },
  ];

  const links = [
    { to: "/", label: "Home", key: "home" },
    { to: "/services", label: t("nav.services"), key: "services" },
    { to: "/focus", label: "Focus", key: "focus" },
    { to: "/team", label: t("nav.team"), key: "team" },
    { to: "/therabody", label: t("nav.therabody"), key: "therabody" },
    { to: "/blog", label: t("nav.journal"), key: "journal" },
    { to: "/contact", label: t("nav.contact"), key: "contact" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-50 bg-white border-b border-[#e5e7eb]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="nav-logo-link"
          className="flex items-center gap-3"
          onClick={() => setOpen(false)}
        >
          <img
            src={ASSETS.ashlanLogo}
            alt="Ashlan Clinic"
            className="h-9 w-auto object-contain"
          />
          <span className="sr-only">Ashlan Clinic</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.key}`}
              className={({ isActive }) =>
                `text-[0.78rem] uppercase tracking-[0.18em] font-semibold transition-colors ${
                  isActive
                    ? "text-[#080808]"
                    : "text-[#3C4048] hover:text-[#080808]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LanguageToggle />
          <div
            className="hidden md:block relative"
            ref={staffMenuRef}
            data-testid="nav-staff-portal-wrapper"
          >
            <button
              type="button"
              data-testid="nav-staff-menu-toggle"
              aria-haspopup="menu"
              aria-expanded={staffOpen}
              onClick={() => setStaffOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 text-[0.72rem] uppercase tracking-[0.18em] font-semibold text-[#3A7A8C] hover:text-[#080808] transition-colors px-2 py-2"
            >
              Staff Portal
              <ChevronDown
                size={13}
                strokeWidth={2.5}
                className={`transition-transform ${staffOpen ? "rotate-180" : ""}`}
              />
            </button>
            {staffOpen && (
              <div
                role="menu"
                data-testid="nav-staff-menu"
                className="absolute right-0 top-full mt-2 w-64 bg-white border border-[#e5e7eb] shadow-xl py-2 z-50"
              >
                {staffItems.map((it) => (
                  <a
                    key={it.testid}
                    href={it.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={it.testid}
                    role="menuitem"
                    onClick={() => setStaffOpen(false)}
                    className="flex items-start gap-3 px-4 py-3 hover:bg-[#F5F5F5] transition-colors group"
                  >
                    <it.icon
                      size={18}
                      strokeWidth={1.75}
                      className="text-[#3A7A8C] shrink-0 mt-0.5"
                    />
                    <div className="min-w-0">
                      <div className="text-[0.75rem] uppercase tracking-[0.14em] font-semibold text-[#080808] group-hover:text-[#3A7A8C]">
                        {it.label}
                      </div>
                      <div className="text-xs text-[#6B7280] mt-0.5">{it.hint}</div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
          <Link
            to={BOOKING_URL}
            data-testid="nav-book-now-button"
            className="hidden md:inline-flex ash-btn-primary"
          >
            {t("cta.book_now")}
          </Link>
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="nav-mobile-toggle"
            className="lg:hidden p-2 text-[#080808]"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#e5e7eb] bg-white">
          <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.key}`}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `text-base font-semibold uppercase tracking-[0.18em] ${
                    isActive ? "text-[#080808]" : "text-[#3C4048]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div
              data-testid="nav-mobile-staff-portal"
              className="mt-2 border-t border-[#e5e7eb] pt-4 flex flex-col gap-3"
            >
              <p className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[#6B7280]">
                Staff Portal
              </p>
              {staffItems.map((it) => (
                <a
                  key={it.testid}
                  href={it.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`nav-mobile-${it.testid.replace("nav-", "")}`}
                  onClick={() => setOpen(false)}
                  className="flex items-start gap-3"
                >
                  <it.icon
                    size={18}
                    strokeWidth={1.75}
                    className="text-[#3A7A8C] shrink-0 mt-0.5"
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold uppercase tracking-[0.14em] text-[#080808]">
                      {it.label}
                    </div>
                    <div className="text-xs text-[#6B7280] mt-0.5">{it.hint}</div>
                  </div>
                </a>
              ))}
            </div>
            <Link
              to={BOOKING_URL}
              data-testid="nav-mobile-book-now-button"
              className="ash-btn-primary self-start mt-4"
              onClick={() => setOpen(false)}
            >
              Book Now
            </Link>
          </nav>
        </div>
      )}

      {/* highlight current path subtle bar */}
      <div
        className="h-[2px] bg-[#5FA8B4]"
        style={{ width: pathname === "/" ? "0%" : "0%" }}
      />
    </header>
  );
}
